// European roulette. Click the board to place chips, then spin. The server picks
// the number; the spinning wheel only reveals it.
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api.ts';
import { useGame, money, fmt } from '../state.tsx';
import { Panel } from '../components/ui.tsx';
import { play } from '../fx/sound.ts';
import { fx, centerOf } from '../fx/fx.tsx';
import { getSettings } from '../fx/settings.ts';

// Pocket order on a single-zero wheel, clockwise from zero.
const WHEEL = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26];
const STEP = 360 / WHEEL.length;

function RouletteWheel({ rot, ball, ms, settled, number }: { rot: number; ball: number; ms: number; settled: boolean; number: number | null }) {
  const ease = `transform ${ms}ms cubic-bezier(0.15, 0.6, 0.2, 1)`;
  return (
    <svg className="r-wheel-svg" viewBox="-100 -100 200 200" width="150" height="150" aria-label={number === null ? 'Roulette wheel' : `The ball landed on ${number}`}>
      <circle r="98" fill="#4a2a10" stroke="#2a1404" strokeWidth="3" />
      <circle r="92" fill="#6a3a14" />
      <g style={{ transform: `rotate(${rot}deg)`, transition: ease }}>
        {WHEEL.map((n, i) => {
          const a0 = ((i - 0.5) * STEP - 90) * (Math.PI / 180);
          const a1 = ((i + 0.5) * STEP - 90) * (Math.PI / 180);
          const p = (r: number, a: number) => `${(r * Math.cos(a)).toFixed(2)} ${(r * Math.sin(a)).toFixed(2)}`;
          const am = (i * STEP - 90) * (Math.PI / 180);
          return (
            <g key={n}>
              <path d={`M${p(58, a0)} L${p(88, a0)} A88 88 0 0 1 ${p(88, a1)} L${p(58, a1)} A58 58 0 0 0 ${p(58, a0)}Z`} fill={n === 0 ? '#1a8a2a' : RED.has(n) ? '#b01010' : '#111'} stroke="#c9a040" strokeWidth="0.6" />
              <text x={(78 * Math.cos(am)).toFixed(2)} y={(78 * Math.sin(am)).toFixed(2)} fill="#fff" fontSize="7" fontWeight="bold" textAnchor="middle" dominantBaseline="central" transform={`rotate(${i * STEP} ${(78 * Math.cos(am)).toFixed(2)} ${(78 * Math.sin(am)).toFixed(2)})`}>{n}</text>
            </g>
          );
        })}
        <circle r="58" fill="#5a3010" stroke="#c9a040" strokeWidth="1" />
        {[0, 90, 180, 270].map((a) => <rect key={a} x="-3" y="-50" width="6" height="40" fill="#c9a040" transform={`rotate(${a})`} />)}
        <circle r="12" fill="#e0b84a" stroke="#7a5000" />
      </g>
      <g style={{ transform: `rotate(${ball}deg)`, transition: ease }}>
        <circle r="4.2" fill="#fff" stroke="#999" strokeWidth="0.6" style={{ transform: `translateY(${settled ? -66 : -92}px)`, transition: settled ? `transform ${Math.min(800, ms / 3)}ms ease-in ${Math.max(0, ms * 0.62)}ms` : 'transform 300ms ease-out' }} />
      </g>
      <path d="M0 -99 l-5 -0 l5 9 l5 -9z" fill="#ffcc33" stroke="#7a5000" strokeWidth="0.6" />
    </svg>
  );
}

const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
const color = (n: number) => (n === 0 ? 'green' : RED.has(n) ? 'red' : 'black');
const OUTSIDE: [string, string][] = [['low', '1 to 18'], ['even', 'EVEN'], ['red', 'RED'], ['black', 'BLACK'], ['odd', 'ODD'], ['high', '19 to 36']];
const LABEL: Record<string, string> = {
  low: '1-18', high: '19-36', red: 'Red', black: 'Black', odd: 'Odd', even: 'Even',
  dozen1: '1st 12', dozen2: '2nd 12', dozen3: '3rd 12', col1: '1st column', col2: '2nd column', col3: '3rd column',
};
const PAYS: Record<string, string> = { straight: '35 to 1', dozen: '2 to 1', col: '2 to 1', even: '1 to 1' };

type BetMap = Record<string, { type: string; number?: number; amount: number }>;

export function RouletteTable() {
  const { me, setMe, setFlash } = useGame();
  const [chip, setChip] = useState(1000);
  const [bets, setBets] = useState<BetMap>({});
  const [spinning, setSpinning] = useState(false);
  const [shown, setShown] = useState<number | null>(null);
  const [result, setResult] = useState<any>(null);
  const [lastBets, setLastBets] = useState<BetMap | null>(null);
  const [history, setHistory] = useState<number[]>([]);
  const timer = useRef<number>(0);
  const [wheel, setWheel] = useState({ rot: 0, ball: 0, ms: 0, settled: true });
  useEffect(() => {
    api.get('/api/casino/roulette').then((d) => setHistory(d.history)).catch(() => {});
  }, []);
  if (!me) return null;
  const total = Object.values(bets).reduce((s, b) => s + b.amount, 0);
  const maxBet = Math.min(10_000_000, Math.max(1000, me.level * 25000));

  const place = (key: string, type: string, number?: number) => {
    if (spinning) return;
    if (total + chip > maxBet) return setFlash({ kind: 'bad', text: `The table limit at your level is ${money(maxBet)} per spin.` });
    setResult(null);
    play('chips', { volume: 0.7 });
    setBets((b) => ({ ...b, [key]: { type, number, amount: (b[key]?.amount ?? 0) + chip } }));
  };
  const chipOn = (key: string) => (bets[key] ? <span className="r-chip">{bets[key].amount >= 1000 ? `${Math.round(bets[key].amount / 1000)}K` : bets[key].amount}</span> : null);

  const spin = async () => {
    if (spinning || !total) return;
    setSpinning(true);
    setResult(null);
    setShown(null);
    play('chips');
    try {
      const r = await api.post('/api/casino/roulette/spin', { bets: Object.values(bets) });
      setLastBets(bets);
      // Spin the wheel so the winning pocket ends under the pointer, with the ball dropping into it.
      const motion = getSettings().motion;
      const ms = motion === 'off' ? 300 : motion === 'reduced' ? 2000 : 4200;
      const target = -WHEEL.indexOf(r.result.number) * STEP;
      setWheel((w) => {
        const turn = ((target - w.rot) % 360 + 360) % 360;
        return { rot: w.rot + 360 * 3 + turn, ball: w.ball - 360 * 5 - (((w.ball % 360) + 360) % 360), ms, settled: false };
      });
      window.setTimeout(() => setWheel((w) => ({ ...w, settled: true })), 350);
      play('ball', { duration: ms / 1000 });
      timer.current = window.setTimeout(() => {
        setShown(r.result.number);
        setHistory((h) => [r.result.number, ...h].slice(0, 20));
        setResult(r.result);
        setMe(r.me);
        setSpinning(false);
        const res = r.result;
        if (res.net > 0) {
          play(res.bets.some((b: any) => b.won && b.type === 'straight') ? 'bigWin' : 'win');
          fx.coins(res.bets.some((b: any) => b.won && b.type === 'straight') ? 20 : 10, centerOf(document.querySelector('.r-wheel-wrap')));
          fx.float(`+${money(res.net)}`, 'cash');
        } else if (res.payout > 0) play('coin');
        else { play('lose', { volume: 0.6 }); fx.float(`−${money(res.bets.reduce((s2: number, b: any) => s2 + b.amount, 0))}`, 'loss'); }
      }, ms + 150);
    } catch (e) {
      clearInterval(timer.current);
      play('error');
      setSpinning(false);
      setFlash({ kind: 'bad', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
    }
  };

  const cell = (n: number) => (
    <div key={n} className={`r-num ${color(n)} ${result?.number === n ? 'hit' : ''}`} onClick={() => place(`n${n}`, 'straight', n)} title={`Bet on ${n} (pays 35 to 1)`}>
      {n}{chipOn(`n${n}`)}
    </div>
  );

  return (
    <div>
      <div className="row" style={{ alignItems: 'center', marginBottom: 8 }}>
        <div className="r-wheel-wrap">
          <RouletteWheel {...wheel} number={shown} />
          <div className={`r-wheel ${shown === null ? '' : color(shown)} ${shown === null ? 'blank' : 'shown'}`}>{shown === null ? (spinning ? '…' : '?') : shown}</div>
        </div>
        <div className="grow">
          {result ? (
            <div className={`r-result ${result.net > 0 ? 'win' : ''}`}>
              {result.number} {color(result.number).toUpperCase()} · {result.net > 0 ? <>You win {money(result.payout)}!</> : result.payout > 0 ? <>You get {money(result.payout)} back</> : 'No luck this time'}
              <div className="small muted">{result.bets.map((b: any, i: number) => <span key={i}>{i > 0 && ' · '}{b.type === 'straight' ? b.number : LABEL[b.type]} {money(b.amount)} {b.won ? <b className="good">won {money(b.payout)}</b> : <span className="bad">lost</span>}</span>)}</div>
            </div>
          ) : <div className="muted">{spinning ? 'No more bets… the ball is rolling!' : 'Click the board to place chips, then spin.'}</div>}
        </div>
      </div>
      <div className="r-history" title="Your last numbers, newest first">
        <span className="small dim">Last numbers:</span>
        {history.length === 0 ? <span className="small dim">none yet</span> : history.map((n, i) => <span key={i} className={`r-hist ${color(n)} ${i === 0 ? 'latest' : ''}`}>{n}</span>)}
      </div>
      <div className="r-board">
        <div className="r-zero green" onClick={() => place('n0', 'straight', 0)} title="Bet on 0">0{chipOn('n0')}</div>
        <div className="r-grid">
          {[3, 2, 1].map((row) => Array.from({ length: 12 }, (_, c) => cell(c * 3 + row)))}
        </div>
        <div className="r-cols">
          {['col3', 'col2', 'col1'].map((k) => <div key={k} className="r-out" onClick={() => place(k, k)} title={`${LABEL[k]} pays 2 to 1`}>2:1{chipOn(k)}</div>)}
        </div>
        <div />
        <div className="r-dozens">
          {['dozen1', 'dozen2', 'dozen3'].map((k) => <div key={k} className="r-out" onClick={() => place(k, k)}>{LABEL[k]}{chipOn(k)}</div>)}
        </div>
        <div />
        <div />
        <div className="r-outside">
          {OUTSIDE.map(([k, label]) => <div key={k} className={`r-out ${k === 'red' ? 'red' : k === 'black' ? 'black' : ''}`} onClick={() => place(k, k)}>{label}{chipOn(k)}</div>)}
        </div>
      </div>
      <div className="row" style={{ alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <span>Chip:</span>
        <div className="chips">
          {[100, 1000, 10_000, 100_000, 1_000_000].filter((c) => c <= maxBet).map((c) => (
            <div key={c} className="chip" style={{ background: { 100: '#2a6ad8', 1000: '#222', 10000: '#a01010', 100000: '#6a2a8a', 1000000: '#b8860b' }[c], outline: chip === c ? '3px solid #fff' : 'none' }}
              onClick={() => setChip(c)}>{c >= 1_000_000 ? '1M' : c >= 1000 ? `${c / 1000}K` : c}</div>
          ))}
        </div>
        <span className="grow" />
        <span>On the table: <b className="money">{money(total)}</b> · cash <span className="money">{money(me.cash)}</span></span>
        <button className="btn gray" disabled={spinning} onClick={() => { setBets({}); setResult(null); }}>Clear</button>
        <button className={`btn gray ${lastBets && !spinning ? '' : 'disabled'}`} disabled={!lastBets || spinning} title="Put the same chips on the table as last spin"
          onClick={() => { if (lastBets) { setBets(lastBets); setResult(null); } }}>Repeat bet</button>
        <button className={`btn big green ${total && total <= me.cash && !spinning ? '' : 'disabled'}`} disabled={spinning} onClick={spin}>SPIN</button>
      </div>
      <Panel title="How roulette pays">
        <div className="small">
          Single number <b>{PAYS.straight}</b> · Dozens and columns <b>{PAYS.dozen}</b> · Red/black, odd/even, 1-18/19-36 <b>{PAYS.even}</b>.
          One green zero: if it comes up, every outside bet loses. The house edge is 2.7% on every bet. Table limit {money(maxBet)} per spin; minimum chip $100.
          Your spins so far: {fmt(me.rouletteSpins ?? 0)}.
        </div>
      </Panel>
    </div>
  );
}
